import { useRef, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { JSONContent } from '@tiptap/core';

import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import RichtextInline from 'approot/shared/richtext/richtext-inline';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import { JSONObject } from 'global/graphql/scalars.type';
import { PStyled } from 'approot/shared/styles/typography.style';
import { grey9, red8 } from 'approot/shared/styles/colours.style';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import { SettingSmallText } from 'approot/activity-builder/settings/settings.style';
import { NOTES_BASE_RICHTEXT_SETTINGS } from './teaching-notes-richtext.constants';
import { TeachingNotesDetailRichtextWrapper } from './teaching-notes-detail-richtext-edit.style';

type Props = {
  onChange: (json: JSONContent) => void;
  data: JSONObject | null;
  placeholder?: string;
  id?: string;
  title?: string;
  dataSelector?: string;
  description?: string;
  error?: string;
};

const TeachingNotesDetailRichtextEdit = ({
  id,
  onChange,
  data,
  title,
  placeholder = '',
  description,
  dataSelector = 'tls-section-edit',
  error,
}: Props) => {
  const [editorStateToUpdate, setEditorStateToUpdate] = useState<
    JSONContent | undefined
  >();

  const suggestionRef = useRef<HTMLDivElement | null>(null);
  const currentEditorId = useRef<string | undefined>(id);

  const debounceOnChange = debounce((json: JSONContent) => {
    setEditorStateToUpdate(json);
  }, 300);

  const { editor } = useRichtext({
    onChange: json => debounceOnChange(json),
    settings: {
      ...NOTES_BASE_RICHTEXT_SETTINGS,
      placeholder,
      suggestions: [], // leaving this in to prevent tiptap hashtag-link missing error
      suggestionsDropdownElement: suggestionRef,
    },
    initialValue: data,
  });

  // the use effect allows the latest note object to be passed into the update
  useEffect(() => {
    if (editorStateToUpdate) onChange(editorStateToUpdate);
    setEditorStateToUpdate(undefined);
    // eslint-disable-next-line
  }, [editorStateToUpdate]);

  useEffect(() => {
    // update content when id changes in cases where
    // this component is not destroyed
    if (currentEditorId.current && currentEditorId.current !== id) {
      isRichtextEmpty(data)
        ? editor?.chain().clearContent().run()
        : editor?.chain().setContent(data).run();
      currentEditorId.current = id;
    }
  }, [data, editor, id]);

  return (
    <TeachingNotesDetailRichtextWrapper
      $error={!!error}
      data-selector={dataSelector}
    >
      <PStyled
        marginBottom={spacing4}
        colour={error ? red8 : grey9}
        data-selector="tls-section-title-edit"
      >
        {title}
      </PStyled>

      {editor && <RichtextInline editor={editor} />}
      <div ref={suggestionRef}></div>
      {(description || error) && (
        <SettingSmallText marginTop={4}>
          {error || description}
        </SettingSmallText>
      )}
    </TeachingNotesDetailRichtextWrapper>
  );
};

export default TeachingNotesDetailRichtextEdit;
